import React from "react"
import { observer } from "mobx-react-lite"
import * as yup from "yup"
import { useFormik } from "formik"

import Button from "@components/ui/Button/Button"
import Loader from "@components/ui/Loader/BarLoader"
import { TextAreaInput } from "@components/ui/TextInput/TextInput"
import BaseModal from "@components/modals/components/BaseModal/BaseModal"
import { ModalsTypes } from "@components/modals/constants"
import useModal from "@components/modals/useModal"
import ModalTitle from "@components/modals/components/ModalTitle/ModalTitle"
import Templates from "@components/ui/Templates"
import Rating from "@components/ui/Rating/Rating"
import { DEFAULT_FEEDBACK_RATING } from "@framework/constants/search-results"

import ModalFooterContainer from "../components/ControlFooter/ModalFooterContainer"

import styles from "./AnswerFeedbackModal.module.sass"

const isCommentRequired = (rating: number) => {
  return rating < DEFAULT_FEEDBACK_RATING
}

export const validationSchema = yup.object({
  rating: yup
    .number()
    .label("Rating")
    .required()
    .default(DEFAULT_FEEDBACK_RATING),

  comment: yup
    .string()
    .label("Note")
    .when("rating", {
      is: (rating: number) => isCommentRequired(rating),
      then: yup.string().required(),
      otherwise: yup.string().optional(),
    })
    .max(450, "Must be at max 450 symbols long")
    .default("")
    .trim(),
})

export type FormData = yup.InferType<typeof validationSchema>

export interface AnswerFeedbackModalProps {
  rating?: number
  onSubmit?: (rating: number, message: string) => Promise<void>
  onHide?: () => void
}

export const AnswerFeedbackModal: React.FC<AnswerFeedbackModalProps> = observer(
  ({ rating, onSubmit, onHide }) => {
    const { hideModal } = useModal(ModalsTypes.ANSWER_FEEDBACK_MODAL)

    const [isLoading, setLoading] = React.useState(false)

    const handleSubmit = async (data: FormData) => {
      setLoading(true)
      await onSubmit?.(data.rating, data.comment)
      setLoading(false)
    }

    const handleClose = () => {
      hideModal()
      onHide?.()
    }

    const formik = useFormik<FormData>({
      initialValues: validationSchema.cast({
        rating,
      }),
      validationSchema,
      onSubmit: handleSubmit,
    })

    return (
      <BaseModal
        title={
          <>
            <ModalTitle titleText="Send feedback" />

            <div className={styles.ratings}>
              <span>How helpful was this?</span>
              <Rating
                value={formik.values.rating}
                onChange={(newRating) => {
                  formik.setFieldValue("rating", newRating, true)
                }}
              />
              <span>{getRatingText(formik.values.rating)}</span>
            </div>
          </>
        }
        onClose={handleClose}
      >
        <form onSubmit={formik.handleSubmit}>
          <Templates.RollScript
            gutter="24"
            footerSocket={
              <ModalFooterContainer>
                <Button
                  size="big"
                  disabled={isLoading}
                  variant="outlined"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  disabled={isLoading}
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="big"
                  after={isLoading && <Loader />}
                >
                  Submit
                </Button>
              </ModalFooterContainer>
            }
          >
            <TextAreaInput
              type="textarea"
              name="comment"
              value={formik.values.comment}
              onChange={formik.handleChange}
              placeholder={`Enter your note here${
                isCommentRequired(formik.values.rating)
                  ? " (required)"
                  : " (optional)"
              }`}
              className={styles.input}
              withError={Boolean(
                formik.touched.comment && formik.errors.comment
              )}
              autoFocus
            />
          </Templates.RollScript>
        </form>
      </BaseModal>
    )
  }
)

const getRatingText = (rating: number) => {
  switch (rating) {
    case 1:
      return "Not helpful"
    case 2:
      return "Slightly Helpful"
    case 3:
      return "Moderately Helpful"
    case 4:
      return "Very Helpful"
    case 5:
      return "Extremely Helpful"
    default:
      return "Moderately Helpful"
  }
}

export default AnswerFeedbackModal
